import { ArrowRight, Building2, User } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocalePath, useTranslation } from "@thyra/landing-i18n";
import { getAppUrl } from "../env";

/**
 * Two-audience fork near the top of the home page (THY-95 / LAND-1): the rest
 * of the home page speaks to one person and their own inbox and calendar, so a
 * visitor evaluating Thyra for a team gets routed to /business here, in the
 * first screen after the hero, instead of only by the teaser near the footer.
 */
const AUDIENCES = [
  { id: "personal", Icon: User, href: getAppUrl(), external: true },
  { id: "team", Icon: Building2, href: "/business", external: false },
] as const;

const BULLETS = ["first", "second", "third"] as const;

export function AudienceSplitSection() {
  const { t } = useTranslation("home");
  const lp = useLocalePath();

  return (
    <section
      className="py-16 sm:py-20"
      aria-labelledby="audience-split-heading"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <h2 id="audience-split-heading" className="sr-only">
          {t("audience.heading")}
        </h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {AUDIENCES.map(({ id, Icon, href, external }, i) => {
            const linkClass =
              "btn-brand group mt-6 inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl px-6 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50";
            return (
              <div
                key={id}
                data-aos="fade-up"
                data-aos-delay={i * 90}
                className="marketing-card marketing-card-hover flex h-full flex-col p-6 sm:p-8"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" strokeWidth={2} />
                  </span>
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                    {t(`audience.items.${id}.eyebrow`)}
                  </p>
                </div>
                <h3 className="mt-4 text-xl font-semibold tracking-tight text-card-foreground sm:text-2xl">
                  {t(`audience.items.${id}.title`)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {t(`audience.items.${id}.body`)}
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground-strong">
                  {BULLETS.map((b) => (
                    <li key={b} className="flex items-center gap-2">
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                        aria-hidden
                      />
                      {t(`audience.items.${id}.bullets.${b}`)}
                    </li>
                  ))}
                </ul>
                <div className="flex-1" />
                {external ? (
                  <a href={href} className={linkClass}>
                    {t(`audience.items.${id}.cta`)}
                    <ArrowRight
                      className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                      strokeWidth={2}
                    />
                  </a>
                ) : (
                  <Link to={lp(href)} className={linkClass}>
                    {t(`audience.items.${id}.cta`)}
                    <ArrowRight
                      className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                      strokeWidth={2}
                    />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
